import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import {
  addYears,
  formatISO,
  getYear,
  isBefore,
  isValid,
  set,
  startOfToday,
  sub,
  subYears,
} from 'date-fns';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import { DrawerLayoutWrapper } from 'common';
import AccordionSummary from 'feature/dispatching/components/AccordionSummary';
import CompartmentsForm from './components/CompartmentsForm';
import DocumentsForm from './components/DocumentsForm';
import TrailerDetails from './components/TrailerDetails';
import DownloadTemplatesTooltip from './components/DownloadTemplatesTooltip';
import {
  useEditTrailerMutation,
  useGetNewLocationsForTrailerQuery,
  useViewTrailerQuery,
} from '../trailerStatusEndpoints';

const accordionStyles = {
  border: 1,
  borderColor: 'graphic.light',
  boxShadow: 'none',
};
const accordionDetailsStyles = {
  backgroundColor: 'surface.white',
  p: 0,
  pl: 1,
  pr: 1.5,
};

const initialState = {
  locations: [],
};

const LocationLens = R.lensProp('locations');

// prettier-ignore
const isTrailerExpired = trailer => isBefore(
  new Date(trailer.expirationDate), 
  startOfToday()
);

const EditTrailerDrawer = ({ onClose, trailerId }) => {
  const [save, { isLoading: isSaving, isSuccess: isSaveSuccess }] = useEditTrailerMutation();
  const [draftDialogOpened, setDraftDialogOpened] = useState(false);
  const {
    data: trailerQuery = {},
    isLoading,
    isSuccess: isLoadingSuccess,
  } = useViewTrailerQuery({ trailerId });
  const { locations: selectedLocations, vaporTightnessCode, status } = trailerQuery;
  const isExpired = R.or(R.isEmpty(trailerQuery), isTrailerExpired(trailerQuery));
  const [locationState, setLocationState] = useState(initialState);
  const [trailerState, setTrailerState] = useState(trailerQuery);
  const [validationText, setValidationText] = useState('');
  const [tempTrailerState, setTempTrailerState] = useState({});

  const params = { trailerId };

  const { data = [] } = useGetNewLocationsForTrailerQuery({ params });

  const {
    compartments = [],
    documents = {},
    effectiveDate = '',
    expirationDate = '',
    hasMexicanTrailerNumber = !!trailerQuery.mexicanTrailerNumber,
    locations = [],
    mexicanTrailerNumber = '',
  } = trailerState;

  const isInvalid = R.anyPass([
    R.isEmpty,
    R.o(R.isNil, R.path(['documents', 'method27', 'documentId'])),
    R.o(R.isNil, R.path(['documents', 'wetTest', 'documentId'])),
    R.compose(R.gt(1), R.length, R.prop(compartments)),
    R.compose(
      R.any(R.equals('')),
      R.map(R.prop('capacityInGallons')),
      R.defaultTo([]),
      R.prop('compartments')
    ),
    R.compose(
      R.any(R.gt(1)),
      R.map(R.prop('capacityInGallons')),
      R.defaultTo([]),
      R.prop('compartments')
    ),
    R.both(
      R.propEq('hasMexicanTrailerNumber', true),
      R.compose(R.isEmpty, R.prop('mexicanTrailerNumber'))
    ),
  ])(trailerState);

  // prettier-ignore
  const handleMethod27Upload = R.compose(
    setTrailerState,
    R.assocPath(['documents', 'method27'])
  );

  // prettier-ignore
  const handleWetTestUpload = R.compose(
    setTrailerState,
    R.assocPath(['documents', 'wetTest']),
  );

  const validateEffectiveDate = date => {
    if (!date) setValidationText('A valid date is Required');
    else if (date < subYears(startOfToday(), 1))
      setValidationText('Must be within 1 year of current date');
    else setValidationText('');
  };

  const evaluateDate = date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const givenDate = new Date(date);
    const currentTime = new Date();
    const currentDate = startOfToday();
    const currentDateTime = set(currentDate, {
      hours: currentTime.getHours(),
      minutes: currentTime.getMinutes(),
      seconds: currentTime.getSeconds(),
    });
    const oneYearAgo = sub(currentDateTime, { years: 1 });
    const dateEval = givenDate.getTime() >= oneYearAgo.getTime() && getYear(date) <= 2099;
    return dateEval;
  };

  const handleAddLocation = location => {
    const over = R.append(location);
    setLocationState(R.over(LocationLens, over));
  };

  const handleRemoveLocation = locId => {
    const over = R.reject(R.equals(locId));
    setLocationState(R.over(LocationLens, over));
  };

  const handleEffectiveDateChange = dateInput => {
    validateEffectiveDate(dateInput);
    if (isValid(dateInput)) {
      const newEffectiveDate = evaluateDate(dateInput) ? formatISO(dateInput) : dateInput;

      setTrailerState(R.assoc('effectiveDate')(newEffectiveDate));
    }
  };

  const handleAddCompartment = () => {
    const lens = R.lensProp('compartments');
    setTrailerState(trailer => {
      return R.over(
        lens,
        R.append({
          capacityInGallons: '0',
          position: R.inc(trailer.compartments.length),
        }),
        trailer
      );
    });
  };

  const handleUpdateCompartment = (index, value) => {
    const path = ['compartments', index, 'capacityInGallons'];
    setTrailerState(R.set(R.lensPath(path), value));
  };

  const handleRemoveCompartment = index => {
    const lens = R.lensProp('compartments');
    const mapIndexed = R.addIndex(R.map);
    const reposition = mapIndexed((c, i) => ({
      ...c,
      position: R.inc(i),
    }));
    const update = R.o(reposition, R.remove(index, 1));
    setTrailerState(R.over(lens, update));
  };

  const handleSave = () => {
    const updateTrailerState = {
      ...trailerState,
      compartments: trailerState.compartments.map(compartment => {
        if (typeof compartment.capacityInGallons === 'string') {
          return {
            ...compartment,
            capacityInGallons: parseInt(compartment.capacityInGallons.replace(/,/g, '')),
          };
        }
        return compartment;
      }),
      expirationDate: formatISO(addYears(new Date(effectiveDate), 1)),
      locations: locations.concat(locationState.locations),
    };
    setTempTrailerState(updateTrailerState);
    R.eqProps('compartments', updateTrailerState, trailerState) &&
    R.eqProps('locations', updateTrailerState, trailerState)
      ? onClose()
      : setDraftDialogOpened(true);
  };

  const handleConfirm = () => {
    save({ trailer: tempTrailerState });
  };

  useEffect(() => {
    const expiredFields = {
      documents: {
        method27: {
          documentId: null,
        },
        wetTest: {
          documentId: null,
        },
      },
    };
    if (isLoadingSuccess) {
      const trailer = R.ifElse(isTrailerExpired, R.mergeDeepLeft(expiredFields), R.identity);
      setTrailerState(trailer(trailerQuery));
    }
  }, [trailerQuery, isLoadingSuccess]);

  useEffect(() => {
    if (isSaveSuccess) onClose();
  }, [isSaveSuccess, onClose]);

  const buttons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
      <Button onClick={onClose} sx={{ width: 150 }} variant="outlined">
        Cancel
      </Button>
      <Button disabled={isInvalid} onClick={handleSave} sx={{ width: 150 }}>
        Save
      </Button>
    </Box>
  );

  return (
    <Drawer anchor="right" open>
      <DrawerLayoutWrapper
        buttons={buttons}
        headerButtonText="CLOSE"
        isLoading={isLoading || isSaving}
        onHeaderButtonClick={onClose}
        title="Edit Trailer"
      >
        {isLoadingSuccess && (
          <Stack sx={{ width: 390 }}>
            <TrailerDetails
              addNewLocation={handleAddLocation}
              effectiveDate={effectiveDate}
              expirationDate={expirationDate}
              hasMexicanTrailerNumber={hasMexicanTrailerNumber}
              isEditing
              isExpired={isExpired}
              locations={selectedLocations}
              locationState={locationState}
              mexicanTrailerNumber={mexicanTrailerNumber}
              removeNewLocation={handleRemoveLocation}
              selectableLocations={data}
              setTrailerState={setTrailerState}
              status={status}
              trailerId={trailerId}
              vaporTightnessCode={vaporTightnessCode}
            />
            <Accordion sx={accordionStyles}>
              <AccordionSummary count={2} label="Documents">
                <DownloadTemplatesTooltip />
              </AccordionSummary>
              <AccordionDetails sx={accordionDetailsStyles}>
                <DocumentsForm
                  documents={documents}
                  effectiveDate={effectiveDate}
                  isExpired={isExpired}
                  onEffectiveDateChange={handleEffectiveDateChange}
                  onMethod27Upload={handleMethod27Upload}
                  onWetTestUpload={handleWetTestUpload}
                  validationText={validationText}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion sx={accordionStyles}>
              <AccordionSummary count={compartments.length} label="Trailer Compartments" />
              <AccordionDetails sx={accordionDetailsStyles}>
                <Box sx={{ ml: 2, mr: 0.25 }}>
                  <CompartmentsForm
                    compartments={compartments}
                    onAddCompartment={handleAddCompartment}
                    onRemoveCompartment={handleRemoveCompartment}
                    onUpdateCompartment={handleUpdateCompartment}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}
        {draftDialogOpened && (
          <Dialog
            maxWidth="xs"
            open
            slotProps={{
              paper: { elevation: 10 },
            }}
            sx={{ '& .MuiDialog-paper': { height: 200, width: 380 } }}
          >
            <DialogTitle>Confirm Changes</DialogTitle>
            <DialogContent>Are you sure you want to make these changes?</DialogContent>
            <DialogActions>
              <Button autoFocus onClick={onClose} variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleConfirm}>Confirm</Button>
            </DialogActions>
          </Dialog>
        )}
      </DrawerLayoutWrapper>
    </Drawer>
  );
};

EditTrailerDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  trailerId: PropTypes.string.isRequired,
};

export default EditTrailerDrawer;
